import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import axios from "axios";
import BASE_URL from "../config";
import {
  CaciStyles, PageHeader, DirectoryCard, SkelDirGrid, SectionNote,
  useReveal, useScrollProgress, useDebounced,
  excerptOf, truncate, langPath, localized, matches,
} from "../components/CaciUI";

/* ═══════════════════════════════════════════════════════════════════════════
   PARTENAIRE — the partner directory
   ───────────────────────────────────────────────────────────────────────────
   Same endpoint (/api/slide/partenaire_all), same external links. Changes:

   · The spinner never appeared. `isLoading` was initialised to false, so on
     first paint the page rendered an empty array and flashed
     "no data available" before the request had even returned. It now starts
     in a loading state and shows skeletons shaped like the real cards.
   · A failed request also showed "no data available", which tells the
     visitor the chamber has no partners rather than that something broke.
     Failure is now its own state, with a retry.
   · Logos were cropped into a 250px photo tile. A logo is not a photograph —
     they now sit contained on a tinted plinth with room to breathe.
   · Descriptions were stripped to plain text and then fed back through
     dangerouslySetInnerHTML with truncation commented out, so one long entry
     could stretch a card far past its neighbours. Plain text, clamped.
   · Added a search field: a directory that runs past a screen needs one, and
     it is accent-insensitive, so "societe" finds "Société".
   ═══════════════════════════════════════════════════════════════════════════ */

function Partenaire() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRtl = lang === "ar";

  const [partenaires, setPartenaires] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term, 250);

  const alive = useRef(true);
  const inputRef = useRef(null);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(() => {
    setState("loading");
    return axios
      .get(`${BASE_URL}/api/slide/partenaire_all`)
      .then((res) => {
        if (!alive.current) return;
        setPartenaires(Array.isArray(res.data) ? res.data : []);
        setState("ready");
      })
      .catch((err) => {
        if (!alive.current) return;
        console.error("Partenaire: failed to load partners —", err);
        setState("error");
      });
  }, []);

  useEffect(() => { load(); }, [load]);

  const titleOf = useCallback(
    (p) => truncate(localized(p, "title", lang), 70),
    [lang]
  );

  /* Filtering runs over the full record, not just the truncated title — a
     partner whose name is cut at 70 characters should still be findable by
     the part that was cut off. */
  const filtered = useMemo(() => {
    if (!debounced) return partenaires;
    return partenaires.filter((p) =>
      matches(localized(p, "title", lang), debounced) ||
      matches(localized(p, "description", lang), debounced)
    );
  }, [partenaires, debounced, lang]);

  const pageRef = useReveal([filtered, state]);
  const progress = useScrollProgress();

  const clearSearch = () => {
    setTerm("");
    inputRef.current && inputRef.current.focus();
  };

  const pageTitle = t("menu.Partenaire");
  const nf = lang === "ar" ? "ar-DZ" : lang === "en" ? "en-US" : "fr-FR";

  return (
    <div className={`caci-page${isRtl ? " is-rtl" : ""}`} ref={pageRef} dir={isRtl ? "rtl" : "ltr"}>
      <CaciStyles />

      <Helmet>
        <title>{pageTitle} — {t("menu.caci")}</title>
        <meta name="description" content={t("home.Partenaire", pageTitle)} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={t("home.Partenaire", pageTitle)} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== "undefined" ? window.location.href : ""} />
      </Helmet>

      <div
        className="caci-progress"
        style={{ transform: `scaleX(${progress})`, opacity: progress > 0.005 ? 1 : 0 }}
        aria-hidden="true"
      />

      <PageHeader
        title={pageTitle}
        sub={t("home.Partenaire_sub", "")}
        crumbs={[{ label: t("menu.home"), to: langPath(lang, "/") }, { label: pageTitle }]}
        count={
          state === "ready" && partenaires.length > 0
            ? { value: partenaires.length.toLocaleString(nf), label: t("menu.Partenaire") }
            : null
        }
      />

      {/* The search bar is only worth its vertical space once the directory is
          long enough to need it. */}
      {state === "ready" && partenaires.length > 8 && (
        <div className="bar">
          <div className="caci-shell">
            <div className="bar-inner">
              <div className="field">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  ref={inputRef}
                  type="search"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={t("search.placeholder")}
                  aria-label={t("search.placeholder")}
                  autoComplete="off"
                  dir="auto"
                />
                {term && (
                  <button
                    type="button"
                    className="field-clear"
                    onClick={clearSearch}
                    aria-label={t("home.Close")}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="caci-section">
        <div className="caci-shell">
          {state === "loading" && <SkelDirGrid count={6} />}

          {state === "error" && (
            <SectionNote
              state="error"
              title={t("home.section_error_title", "Chargement impossible")}
              text={t("home.section_error", "Cette liste n'a pas pu être chargée.")}
              action={
                <button type="button" className="btn-quiet" onClick={load}>
                  <i className="bi bi-arrow-clockwise" />
                  {t("home.retry", "Réessayer")}
                </button>
              }
            />
          )}

          {state === "ready" && partenaires.length === 0 && (
            <SectionNote text={t("home.no_data_available")} />
          )}

          {state === "ready" && partenaires.length > 0 && filtered.length === 0 && (
            <SectionNote
              title={t("search.noResults")}
              text={t("search.tryDifferentTerms")}
              action={
                <button type="button" className="btn-quiet" onClick={clearSearch}>
                  <i className="bi bi-arrow-counterclockwise" />
                  {t("search.clear", "Tout effacer")}
                </button>
              }
            />
          )}

          {state === "ready" && filtered.length > 0 && (
            <div className="caci-grid">
              {filtered.map((p, i) => (
                <DirectoryCard
                  key={p.id ?? i}
                  index={i}
                  href={p.link_url || undefined}
                  logo={p.image ? `${BASE_URL}${p.image}` : null}
                  title={titleOf(p)}
                  description={excerptOf(localized(p, "description", lang), 24)}
                  actionLabel={p.link_url ? t("home.visit_website") : null}
                  isRtl={isRtl}
                />
              ))}
            </div>
          )}

          <p className="caci-sr" aria-live="polite">
            {state === "ready" && debounced
              ? `${filtered.length} ${t("search.results", "résultats")}`
              : ""}
          </p>
        </div>
      </section>
    </div>
  );
}

export default Partenaire;
